import { sizes } from '@lmc/common/constants'
import {
  CarerRole,
  type Home,
  type Need,
  type Skill,
  type SkillCategory,
  type SkillNeedTemplate
} from '@lmc/ts-common'
import {
  Button,
  Chevron,
  EmptyState,
  Icon,
  NoDataIcon,
  Select
} from '@lmc/ui-kit'
import { Text as GeneralText } from '@lmc/ui-kit/src/general'
import { Pagination } from 'antd'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { fetchAllNeeds, NEEDS_QUERY_KEYS, type NeedItem } from 'hooks/useNeeds'
import { type FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import {
  connect,
  type MapDispatchToPropsParam,
  type MapStateToPropsParam
} from 'react-redux'
import type { SkillNeedTemplateResponse } from 'screens/Organisation/screens/TrainingMatrixScreen/components/DueOverdueTrainingMatrix'
import { getNeedsAndSkillsTemplates } from 'selectors/NeedsAndSkills'
import styled from 'styled-components'
import Operations from '../../../operations'
import {
  SkillNeedTemplateHeaderSection,
  SkillNeedTemplateModal,
  SkillNeedTemplateTable
} from './components'
import { DeleteSkillTemplateModal } from './components/DeleteSkillTemplateModal'

interface StateProps {
  skillOptions?: Skill[]
  needOptions?: Need[]
  categoriesOptions?: SkillCategory[]
  skillNeedTemplates?: SkillNeedTemplate[]
  preferredPeopleNominative?: string
  role?: string
  home?: Home
  homeGroup?: any
}

interface DispatchProps {
  fetchSkillOptions?: () => Promise<unknown>
  fetchCategoriesOptions?: () => Promise<unknown>
  fetchNeedsOptions?: () => Promise<unknown>
  fetchSkillNeedTemplates?: () => Promise<SkillNeedTemplateResponse[]>
}

type Props = StateProps & DispatchProps

export const SkillTemplateScreen: FC<Props> = ({
  fetchSkillOptions,
  fetchCategoriesOptions,
  fetchNeedsOptions,
  fetchSkillNeedTemplates,
  skillNeedTemplates,
  skillOptions = [],
  needOptions = [],
  categoriesOptions = [],
  preferredPeopleNominative,
  role,
  home,
  homeGroup
}) => {
  const { t } = useTranslation()

  const flags = useFeatureFlags({ flagNames: ['mods_needs_settings'] })?.data
  const isNeedsCarePlanEnabled = flags?.mods_needs_settings?.enabled ?? false

  const { data: allNeeds } = useQuery<NeedItem[]>(
    [NEEDS_QUERY_KEYS.options],
    fetchAllNeeds,
    { retry: false, retryOnMount: false, enabled: isNeedsCarePlanEnabled }
  )

  const mergedNeedOptions = useMemo(() => {
    if (!isNeedsCarePlanEnabled || !allNeeds?.length) return needOptions
    const legacyIds = new Set(needOptions.map((n) => n.id))
    const merged = [...needOptions]
    allNeeds.forEach((n) => {
      if (!legacyIds.has(n.id)) {
        merged.push(n as unknown as Need)
      }
    })
    return merged
  }, [needOptions, allNeeds, isNeedsCarePlanEnabled])

  const [open, setOpen] = useState(false)
  const [openDelete, setOpenDelete] = useState(false)
  const [editTemplate, setEditTemplate] = useState<SkillNeedTemplate>()
  const [deleteTemplate, setDeleteTemplate] = useState<SkillNeedTemplate>()

  const handleToggleModal = () => {
    if (open) {
      setEditTemplate(undefined)
    }
    setOpen(!open)
  }

  const handleOpenEdit = (template: SkillNeedTemplate) => {
    setEditTemplate(template)
    setOpen(true)
  }

  const handleOpenDelete = (template: SkillNeedTemplate) => {
    setDeleteTemplate(template)
    setOpenDelete(true)
  }

  const handleCloseDelete = () => {
    setDeleteTemplate(undefined)
    setOpenDelete(false)
  }

  const handleRefetchOptions = () => {
    fetchSkillOptions?.()
    fetchCategoriesOptions?.()
    fetchNeedsOptions?.()
    fetchSkillNeedTemplates?.()
  }

  useEffect(() => {
    handleRefetchOptions()
  }, [])

  const [sortIndex, setSortIndex] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const PAGE_SIZE = 10

  const sortedTemplates = useMemo(() => {
    if (!skillNeedTemplates?.length) return skillNeedTemplates ?? []
    const sorted = [...skillNeedTemplates]

    // Pre-compute a name lookup so we don't call Array.find() on every comparison
    const skillNameMap = new Map(
      skillOptions.map((s) => [s.id, s.name?.toLowerCase() ?? ''])
    )
    const compareBySkillName = (a: SkillNeedTemplate, b: SkillNeedTemplate) => {
      const nameA = skillNameMap.get(a.skill as string) ?? ''
      const nameB = skillNameMap.get(b.skill as string) ?? ''
      return nameA.localeCompare(nameB)
    }

    switch (sortIndex) {
      case 0: // newest first
        return sorted.sort(
          (a, b) =>
            new Date(b.createdOn ?? 0).getTime() -
            new Date(a.createdOn ?? 0).getTime()
        )
      case 1: // oldest first
        return sorted.sort(
          (a, b) =>
            new Date(a.createdOn ?? 0).getTime() -
            new Date(b.createdOn ?? 0).getTime()
        )
      case 2: // skill name A-Z
        return sorted.sort(compareBySkillName)
      case 3: // skill name Z-A
        return sorted.sort((a, b) => compareBySkillName(b, a))
      default:
        return sorted
    }
  }, [skillNeedTemplates, sortIndex, skillOptions])

  const hasSkillsNeedsTemplates = !!sortedTemplates?.length
  const totalTemplates = skillNeedTemplates?.length ?? 0
  const paginatedTemplates = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE
    return sortedTemplates.slice(start, start + PAGE_SIZE)
  }, [sortedTemplates, currentPage])

  const sortingOptions = [
    { label: t('skillsAndNeeds.sortNewestFirst'), value: '0' },
    { label: t('skillsAndNeeds.sortOldestFirst'), value: '1' },
    { label: t('skillsAndNeeds.sortSkillNameAZ'), value: '2' },
    { label: t('skillsAndNeeds.sortSkillNameZA'), value: '3' }
  ]

  const createdNeedsIds = skillNeedTemplates?.flatMap(
    (skillNeedTemplate) => skillNeedTemplate.needs
  )

  const createdSkillIds = skillNeedTemplates?.map(
    (skillNeedTemplate) => skillNeedTemplate.skill
  )

  const isHomeGroup = home?.homeGroup || homeGroup?.id

  const isGroupRole =
    role &&
    [CarerRole.groupAdmin, CarerRole.groupManager].includes(role as CarerRole)

  const isHomeAdminRole = role === CarerRole.careHomeAdmin

  const allowCreateEditTemplates =
    (isHomeGroup && isGroupRole) || (!isHomeGroup && isHomeAdminRole)

  return (
    <>
      <DeleteSkillTemplateModal
        isModalVisible={openDelete}
        onClose={handleCloseDelete}
        template={deleteTemplate}
        onRefetch={handleRefetchOptions}
        preferredPeopleNominative={preferredPeopleNominative}
      />
      <SkillNeedTemplateModal
        isModalVisible={open}
        handleToggleModal={handleToggleModal}
        categoriesOptions={categoriesOptions}
        needOptions={needOptions}
        skillOptions={skillOptions}
        skillNeedTemplate={editTemplate}
        onRefetch={handleRefetchOptions}
        createdNeedsIds={createdNeedsIds as string[]}
        createdSkillIds={createdSkillIds as string[]}
      />
      <div style={styles.container}>
        <SkillNeedTemplateHeaderSection
          handleToggleModal={handleToggleModal}
          allowCreateEditTemplates={allowCreateEditTemplates}
          preferredPeopleNominative={preferredPeopleNominative}
        />
        {!hasSkillsNeedsTemplates ? (
          <EmptyState
            title=""
            icon={<NoDataIcon />}
            description={t('skillsAndNeeds.addSkill')}
          >
            {allowCreateEditTemplates && (
              <Button
                onClick={handleToggleModal}
                icon={<Icon name="PlusOutlined" />}
                variant="primary"
              >
                {t('skillsAndNeeds.addNewSkill')}
              </Button>
            )}
          </EmptyState>
        ) : (
          <>
            <StyledSkillTableContainer>
              <StyledTableHeader>
                <StyledSkillsCount level={1}>
                  {t('skillsAndNeeds.skillsCount', {
                    current: paginatedTemplates.length,
                    total: totalTemplates
                  })}
                </StyledSkillsCount>
                <StyledSortWrapper>
                  <StyledSortLabel weight="bold">
                    {t('skillsAndNeeds.sortBy')}
                  </StyledSortLabel>
                  <StyledSortSelect
                    placeholder={
                      <StyledOptionText weight="regular">
                        {sortingOptions[0].label}
                      </StyledOptionText>
                    }
                    suffixIcon={<Chevron direction="down" />}
                    onChange={(value) => {
                      setSortIndex(Number(value))
                      setCurrentPage(1)
                    }}
                  >
                    {sortingOptions.map((option) => (
                      <Select.Option
                        key={option.value}
                        value={option.value}
                        label={option.label}
                      >
                        <StyledOptionText weight="regular">
                          {option.label}
                        </StyledOptionText>
                      </Select.Option>
                    ))}
                  </StyledSortSelect>
                </StyledSortWrapper>
              </StyledTableHeader>
              <SkillNeedTemplateTable
                skillOptions={skillOptions}
                needOptions={mergedNeedOptions}
                skillNeedTemplates={paginatedTemplates}
                categoriesOptions={categoriesOptions}
                setTemplateDetail={handleOpenEdit}
                onDeleteTemplate={handleOpenDelete}
                allowCreateEditTemplates={allowCreateEditTemplates}
              />
            </StyledSkillTableContainer>
            <StyledPaginationWrapper>
              <Pagination
                current={currentPage}
                pageSize={PAGE_SIZE}
                total={totalTemplates}
                onChange={(page) => setCurrentPage(page)}
                showSizeChanger={false}
              />
            </StyledPaginationWrapper>
          </>
        )}
      </div>
    </>
  )
}

const styles = {
  container: {
    margin: '0 auto',
    maxWidth: sizes.childScreen,
    padding: '24px 24px'
  }
}

const StyledTableHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 16px;
`

const StyledSkillsCount = styled(GeneralText)`
  color: ${({ theme }) => theme.color.neutral.color4} !important;
`

const StyledSortWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const StyledSortLabel = styled(GeneralText)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const StyledSortSelect = styled(Select)`
  width: 290px;
` as typeof Select

const StyledOptionText = styled(GeneralText)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const StyledPaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`

const StyledSkillTableContainer = styled.div`
  overflow: hidden;
  border-radius: 20px;

  .ant-table-thead > tr > th {
    padding-top: 8px !important;
    padding-bottom: 8px !important;
    color: ${({ theme }) => theme.color.neutral.color4} !important;
    font-weight: 500 !important;
  }

  .ant-table-tbody > tr:nth-child(odd) > td {
    background: ${({ theme }) => theme.color.neutral.color11};
  }

  .ant-table-tbody > tr:nth-child(even) > td {
    background: ${({ theme }) => theme.color.neutral.color10};
  }

  .ant-table-tbody > tr > td {
    color: ${({ theme }) => theme.color.neutral.color3} !important;
    font-weight: 500 !important;
    vertical-align: top !important;
    font-size: 15px !important;
  }

  .ant-table-thead > tr > th:nth-child(4),
  .ant-table-tbody > tr > td:nth-child(4) {
    min-width: 130px;
  }
`

const mapStateToProps = (state: {
  data: {
    skills: Skill[]
    ['skill-categories']: SkillCategory[]
    needs: Need[]
    ['skill-need-templates']: SkillNeedTemplate[]
  }
  global: { home: Home; user: { role: string }; homeGroup?: any }
}) => ({
  skillOptions: state.data?.skills,
  categoriesOptions: state.data?.['skill-categories'],
  needOptions: state.data?.needs,
  skillNeedTemplates: getNeedsAndSkillsTemplates(state),
  preferredPeopleNominative: state.global.home.preferredPeopleNominative,
  role: state.global.user.role,
  home: state.global.home,
  homeGroup: state.global?.homeGroup
})

const mapDispatchToProps = (dispatch: (arg0: unknown) => unknown) => ({
  fetchSkillOptions: () => dispatch(Operations.loadList('skills')),
  fetchCategoriesOptions: () =>
    dispatch(Operations.loadList('skill-categories')),
  fetchNeedsOptions: () => dispatch(Operations.loadList('needs')),
  fetchSkillNeedTemplates: () =>
    dispatch(Operations.loadList('skill-need-templates'))
})

export default connect<StateProps, DispatchProps, Props>(
  mapStateToProps as MapStateToPropsParam<StateProps, Props, unknown>,
  mapDispatchToProps as MapDispatchToPropsParam<DispatchProps, Props>
)(SkillTemplateScreen)
